/**
 * UnifiedBillingNotifications — Iter-163 · Phase 5.
 *
 * One consolidated notification panel that lives inside the Billing
 * & A/R tab (both PM and Finance views). Reads from the unified
 * /api/billing-notifications endpoints. Color-coded per event type
 * using the billing status color system.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Bell, CheckCircle2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_HEX = {
  GREEN: '#10b981', BLUE: '#3b82f6', AMBER: '#f59e0b',
  YELLOW: '#facc15', ORANGE: '#f97316', RED: '#ef4444',
};

const TYPE_LABEL = {
  invoice_due:           'Invoice due',
  grace_period_warning:  'Grace period',
  invoice_overdue:       'Overdue',
  autopay_success:       'Autopay charged',
  autopay_failed:        'Autopay failed',
  autopay_retry_failed:  'Retry failed',
  check_received:        'Check received',
  payment_confirmed:     'Payment confirmed',
};

export default function UnifiedBillingNotifications({ scope = 'finance', property_id, onChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = property_id ? `?property_id=${encodeURIComponent(property_id)}&limit=40` : '?limit=40';
      const r = await fetch(`${API}/api/billing-notifications${qs}`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setNotes(d.notifications || []); }
    } catch (_) {}
    setLoading(false);
    onChange && onChange();
  }, [property_id, onChange]);
  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    await fetch(`${API}/api/billing-notifications/mark-read`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mark_all: true, property_id }),
    });
    await load();
  };
  const markOne = async (id) => {
    await fetch(`${API}/api/billing-notifications/mark-read`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [id], property_id }),
    });
    await load();
  };

  const unread = notes.filter((n) => !n.read).length;

  return (
    <Card className="p-4 bg-[#141414] border-zinc-800 space-y-3"
          data-testid={`unified-billing-notifications-${scope}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-400" />
          <p className="text-[10px] uppercase tracking-widest text-zinc-300">
            Billing notifications
            <span className="ml-2 text-white font-bold" data-testid="unified-billing-unread-count">
              {unread} unread
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={load}
                  className="text-[10px] uppercase"
                  style={{ backgroundColor: '#27272a', color: '#fff' }}
                  data-testid="unified-billing-refresh">
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={markAllRead} disabled={unread === 0}
                  className="text-[10px] uppercase font-bold"
                  style={{ backgroundColor: '#10b981', color: '#fff' }}
                  data-testid="unified-billing-mark-all">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Mark all read
          </Button>
        </div>
      </div>
      <div className="rounded border border-zinc-800 overflow-x-auto">
        <table className="admin-stack-table text-xs" data-testid="unified-billing-table">
          <thead className="bg-black/40 text-zinc-400 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Property</th>
              <th className="text-left p-2">Invoice</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-left p-2">When</th>
              <th className="text-center p-2">Channels</th>
              <th className="text-center p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-4 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && notes.length === 0 && <tr><td colSpan="7" className="p-4 text-center text-zinc-500">No billing notifications yet.</td></tr>}
            {notes.map((n) => (
              <tr key={n.notification_id} className="border-t border-zinc-800"
                  data-testid={`unified-billing-row-${n.notification_id}`}>
                <td className="p-2" data-label="Type">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap"
                        style={{ backgroundColor: COLOR_HEX[n.status_color] || '#71717a',
                                 color: (n.status_color === 'YELLOW' || n.status_color === 'AMBER') ? '#111' : '#fff' }}
                        data-testid={`unified-billing-type-${n.notification_id}`}>
                    {TYPE_LABEL[n.type] || n.type}
                  </span>
                </td>
                <td className="p-2 text-zinc-300" data-label="Property">{n.property_id || '—'}</td>
                <td className="p-2 text-zinc-300" data-label="Invoice">{n.invoice_number || '—'}</td>
                <td className="p-2 text-right text-white" data-label="Amount">
                  {typeof n.amount === 'number' ? `$${n.amount.toLocaleString()}` : '—'}
                </td>
                <td className="p-2 text-zinc-400" data-label="When">{(n.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                <td className="p-2 text-center text-[10px]" data-label="Channels">
                  <span className={n.channels?.in_app ? 'text-emerald-400' : 'text-zinc-600'}>APP</span>{' · '}
                  <span className={n.channels?.email ? 'text-emerald-400' : 'text-zinc-600'}>EM</span>{' · '}
                  <span className={n.channels?.sms ? 'text-emerald-400' : 'text-zinc-600'}>SMS</span>
                </td>
                <td className="p-2 text-center" data-label="Action">
                  {n.read
                    ? <span className="text-[10px] text-zinc-500 uppercase">Read</span>
                    : <Button size="sm" onClick={() => markOne(n.notification_id)}
                              className="text-[9px] uppercase"
                              style={{ backgroundColor: '#3f3f46', color: '#fff' }}
                              data-testid={`unified-billing-mark-${n.notification_id}`}>
                        Mark read
                      </Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
